import { useQuery } from '@tanstack/react-query';
import {
  listAllParametros,
  regaloMinimoThreshold,
} from '@/shared/api/controllers/parametros';
import { useActiveCountry } from '@/shared/countries/ActiveCountryProvider';
import type { Moneda } from '../components/MoneyCell';

/**
 * Minimum cotización Total (con IVA) that unlocks the gift ("Regalo")
 * button, for the active country and cotización currency. Reads the
 * country-scoped Parámetros del Sistema list and picks `Monto Minimo
 * Regalo` (local ARS/MXN) or `Monto Minimo Regalo USD` (USD).
 *
 * Shares the query key with the Configuración → Parámetros del Sistema
 * CRUD (`['configuracion', 'parametros-sistema', <code>]`) so editing a
 * value there refreshes the gate. Returns 0 while loading or when the
 * parameter is unset (= gifting allowed, feature inert).
 */
export function useRegaloMinimoThreshold(moneda: Moneda): number {
  const { code } = useActiveCountry();
  const { data } = useQuery({
    queryKey: ['configuracion', 'parametros-sistema', code],
    queryFn: listAllParametros,
    staleTime: 60_000,
  });
  return regaloMinimoThreshold(data ?? [], moneda);
}

/**
 * Product type an article must be in order to be giftable. Keyed off the
 * product taxonomy (`TipoProducto_ART`), NOT the accounting Cuenta Contable —
 * so reclassifying the CC (e.g. Accesorios -> Insumos) doesn't change gift
 * eligibility. Only accessories may be given as a regalo.
 */
export const REGALO_TIPO_PRODUCTO = 'Accesorios';

/**
 * Whether an article qualifies as a regalo — i.e. its product type is
 * `Accesorios`. Accepts anything carrying `fields.TipoProducto_ART`
 * (e.g. an `ArticuloItem`); null/undefined ⇒ not giftable.
 */
export function isArticuloRegalable(
  art: { fields?: { TipoProducto_ART?: string | null } } | null | undefined,
): boolean {
  return (art?.fields?.TipoProducto_ART?.trim() ?? '') === REGALO_TIPO_PRODUCTO;
}
